"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { connectModalStore } from '../../../lib/walletConnect/connectModalStore';
const ConnectModalContext = createContext(null);
export function WalletModalProvider({ children }) {
    const [connectConfig, setConnectConfig] = useState(undefined);
    const [selectedProvider, setSelectedProvider] = useState(undefined);
    const [selectedConnector, setSelectedConnector] = useState(undefined);
    const [selectedMultiChainConnector, setSelectedMultiChainConnector] = useState(undefined);
    const [open, setOpen] = useState(false);
    const [presentation, setPresentation] = useState('modal');
    const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
    const [dismissible, setDismissible] = useState(true);
    const [topContent, setTopContent] = useState(null);
    const [fullHeight, setFullHeight] = useState(false);
    const [hideHeader, setHideHeader] = useState(false);
    const connect = useCallback(async ({ provider, connectCallback, dismissible: dismissibleArg = true, topContent: topContentArg = null, fullHeight: fullHeightArg = false, hideHeader: hideHeaderArg = false }) => {
        const hasConnectorPicker = !!provider?.availableConnectors?.length
            || !!provider?.additionalConnectors?.length
            || !!provider?.requestAdditionalConnectors;
        if (!hasConnectorPicker) {
            await provider?.connectWallet();
        }
        // connect() is the modal entry point; inline hosts drive the machine
        // directly via setPresentation('inline'). Force modal here so a prior
        // inline session can't leak into a programmatic connect().
        setPresentation('modal');
        setSelectedProvider(provider);
        setDismissible(dismissibleArg);
        setTopContent(topContentArg);
        setFullHeight(fullHeightArg);
        setHideHeader(hideHeaderArg);
        setOpen(true);
        setConnectConfig({ provider, connectCallback, dismissible: dismissibleArg, topContent: topContentArg, fullHeight: fullHeightArg, hideHeader: hideHeaderArg });
        return;
    }, []);
    const cancel = useCallback(() => {
        setConnectConfig(prev => {
            prev?.connectCallback(undefined);
            return undefined;
        });
        setOpen(false);
    }, []);
    const onFinish = useCallback((connectedWallet) => {
        setConnectConfig(prev => {
            prev?.connectCallback(connectedWallet);
            return undefined;
        });
        setOpen(false);
    }, []);
    const goBack = useCallback(() => {
        if (selectedConnector) {
            setSelectedConnector(undefined);
            setSelectedMultiChainConnector(undefined);
            return;
        }
        else if (selectedMultiChainConnector) {
            setSelectedMultiChainConnector(undefined);
            return;
        }
    }, [selectedConnector, selectedMultiChainConnector]);
    useEffect(() => {
        if (!open && (selectedConnector || selectedMultiChainConnector)) {
            setSelectedConnector(undefined);
            setSelectedMultiChainConnector(undefined);
            setSelectedProvider(undefined);
        }
        if (!open) {
            setDismissible(true);
            setTopContent(null);
            setFullHeight(false);
            setHideHeader(false);
            setPresentation('modal');
        }
        setIsWalletModalOpen(open);
        connectModalStore._syncOpen(open);
    }, [open]);
    useEffect(() => {
        connectModalStore._syncSelectedConnector(selectedConnector);
    }, [selectedConnector]);
    useEffect(() => {
        return connectModalStore._registerWriter((connector) => {
            setSelectedConnector(connector);
        });
    }, [setSelectedConnector]);
    const contextValue = useMemo(() => ({
        connect, cancel, selectedProvider, setSelectedProvider,
        selectedConnector, setSelectedConnector,
        selectedMultiChainConnector, setSelectedMultiChainConnector,
        isWalletModalOpen, goBack, onFinish, setOpen, open, presentation, setPresentation, dismissible, topContent, fullHeight, hideHeader
    }), [connect, cancel, selectedProvider, selectedConnector,
        selectedMultiChainConnector, isWalletModalOpen, goBack, onFinish, open, presentation, dismissible, topContent, fullHeight, hideHeader]);
    return (_jsx(ConnectModalContext.Provider, { value: contextValue, children: children }));
}
export const useConnectModal = () => {
    const context = useContext(ConnectModalContext);
    if (!context) {
        throw new Error('useConnectModal must be used within a ConnectModalProvider');
    }
    const connect = useCallback((provider, options) => new Promise((res) => {
        context.connect({ provider, connectCallback: res, dismissible: options?.dismissible, topContent: options?.topContent, fullHeight: options?.fullHeight, hideHeader: options?.hideHeader });
    }), [context.connect]);
    return useMemo(() => ({ ...context, connect }), [context, connect]);
};
